/**
 * SMARD price API (Germany/Luxembourg, quarterhour resolution)
 *
 * The API works in two steps:
 *  1. Fetch an index of available weekly chunk timestamps:
 *     GET /app/chart_data/{filter}/DE/index_quarterhour.json
 *     → { timestamps: [ms, ms, ...] }
 *
 *  2. Fetch each weekly chunk:
 *     GET /app/chart_data/{filter}/DE/{filter}_DE_quarterhour_{timestamp}.json
 *     → { series: [[ms, price_eur_mwh | null], ...] }
 *
 * Prices are in €/MWh; we convert to €/kWh (÷ 1000).
 * Requests go through the /smard-api proxy (Vite in dev, Nginx in production) to avoid CORS.
 *
 * Filter IDs:
 *   4169 — Day-Ahead prices (EPEX Spot)
 *   4170 — Intraday Continuous average (EPEX Spot)
 */

export interface PricePoint {
  timestampMs: number
  priceEurKwh: number
}

interface SmardIndex {
  timestamps: number[]
}

interface SmardChunk {
  series: Array<[number, number | null]>
}

const SMARD_BASE = '/smard-api/app/chart_data'

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000

/** Permitted SMARD filter IDs. Validated before use in URLs. */
const ALLOWED_FILTERS = new Set([4169, 4170])

function assertFilter(filter: number): void {
  if (!ALLOWED_FILTERS.has(filter)) {
    throw new Error(`SMARD: unsupported filter ID ${filter}`)
  }
}

function assertTimestamp(ts: number): void {
  if (!Number.isSafeInteger(ts) || ts < 0) {
    throw new Error(`SMARD: invalid chunk timestamp ${ts}`)
  }
}

async function fetchIndex(filter: number): Promise<number[]> {
  assertFilter(filter)
  const res = await fetch(`${SMARD_BASE}/${filter}/DE/index_quarterhour.json`) // nosec gitlab.nodejs_scan.javascript-ssrf-rule-node_ssrf -- filter is validated against allowlist; SMARD_BASE is hardcoded
  if (!res.ok) throw new Error(`SMARD index error ${filter}: ${res.status} ${res.statusText}`)
  const json: SmardIndex = await res.json()
  return json.timestamps
}

async function fetchChunk(filter: number, timestamp: number): Promise<Array<[number, number | null]>> {
  assertFilter(filter)
  assertTimestamp(timestamp)
  const url = `${SMARD_BASE}/${filter}/DE/${filter}_DE_quarterhour_${timestamp}.json`
  const res = await fetch(url) // nosec gitlab.nodejs_scan.javascript-ssrf-rule-node_ssrf -- filter and timestamp are validated against allowlist; SMARD_BASE is hardcoded
  if (!res.ok) throw new Error(`SMARD chunk error ${filter}: ${res.status} ${res.statusText}`)
  const json: SmardChunk = await res.json()
  return json.series
}

async function fetchSMARDPrice(filter: number, start: Date, end: Date): Promise<PricePoint[]> {
  const startMs = start.getTime()
  const endMs = end.getTime()

  const allTimestamps = await fetchIndex(filter)
  const relevant = allTimestamps.filter((ts) => ts < endMs && ts + ONE_WEEK_MS > startMs)

  if (relevant.length === 0) return []

  const chunks = await Promise.all(relevant.map((ts) => fetchChunk(filter, ts)))

  const points: PricePoint[] = []
  for (const series of chunks) {
    for (const [ts, price] of series) {
      if (price === null) continue
      if (ts < startMs || ts >= endMs) continue
      points.push({ timestampMs: ts, priceEurKwh: price / 1000 })
    }
  }

  const seen = new Set<number>()
  return points
    .filter((p) => {
      if (seen.has(p.timestampMs)) return false
      seen.add(p.timestampMs)
      return true
    })
    .sort((a, b) => a.timestampMs - b.timestampMs)
}

export function fetchDAPrice(start: Date, end: Date): Promise<PricePoint[]> {
  return fetchSMARDPrice(4169, start, end)
}

export function fetchIntradayPrice(start: Date, end: Date): Promise<PricePoint[]> {
  return fetchSMARDPrice(4170, start, end)
}
